import { EMPLOYEES_BULK_CREATE_FILE_MAX_LINE } from '@Common/constants/b2b/employees';
import type {
  B2bEmployeeDocumentsListItem,
  B2bEmployeesDocumentsTypeCommon,
  GetB2bEmployeesOneResponse,
  PostCreateB2bEmployeeRequest,
} from '@Common/types/bo/b2b/employees';
import { UserDocumentSource } from '@Common/types/bo/users_documents';

const status: Record<GetB2bEmployeesOneResponse['status'], string> = {
  current: 'Current',
  past: 'Past',
  retired: 'Retired',
};

const socialStatus: Record<GetB2bEmployeesOneResponse['social_status'], string> = {
  TS: 'TS',
  TNS: 'TNS',
};

const leavingReason: Record<Exclude<GetB2bEmployeesOneResponse['leaving_reason'], null>, string> = {
  decease: 'Decease',
  job_transfer: 'Job transfer',
  retirement: 'Retirement',
  work_ended: 'Work ended',
  other: 'Other',
};

export const b2bEmployeesTranslations = {
  documents: {
    fields: {
      id: 'Document ID',
      employee_id: 'Employee ID',
      document_type: 'Type',
      document_url: 'URL',
      document_name: 'Name',
      source: 'Source',
      is_archived: 'Archived',
      task_id: 'Task ID',
      created_at: 'Created at',
      updated_at: 'Updated at',
    } satisfies Record<keyof B2bEmployeeDocumentsListItem, string>,
    type: {
      account_statement: 'Account statement',
      trade_confirmation: 'Trade confirmation',
      early_withdrawal_request: 'Early withdrawal request',
      w8ben: 'W8BEN',
      annual_statement_csv: 'Annual statement (csv)',
      bank_account_statement: 'Bank account statement',
      proof_of_address: 'Proof of address',
      account_statement_equities: 'Account statement (equities)',
      rib: 'RIB',
      funding_source: 'Funding source',
      employee_savings_sepa_mandate_form_unsigned: 'Employee Savings SEPA Mandate Form Unsigned',
      employee_savings_sepa_mandate_form_signed: 'Employee Savings SEPA Mandate Form Signed',
      proof_of_identity: 'Identity document',
      axa_individual_transfer_bulletin: 'AXA Individual Transfer Bulletin',
      axa_taiga_individual_transfer_form: 'AXA Taiga Individual Transfer Form',
      axa_voluntary_payment_sof_certificate: 'AXA Voluntary Payment SOF Certificate',
      b2b_account_statement_on_demand: 'Account Statement on-demand',
      b2b_transfer_in_statement: 'B2B Transfer In Statement',
      b2b_employee_welcome_letter: 'B2B Employee Welcome Letter',
      b2b_ca_shareholder_letter: 'CA Shareholder Letter',
      b2b_ca_dividend_statement: 'CA Dividend Statement',
      b2b_campaign_action_required_letter: 'Campaign action required letter',
      b2b_campaign_confirmation: 'Campaign confirmation',
      b2b_shareplan_operation_statement: 'Shareplan operation statement',
    } satisfies Record<B2bEmployeesDocumentsTypeCommon, string>,
    sources: {
      app: 'Application',
      bo: 'Backoffice',
      alpaca: 'Alpaca',
      fourthline: 'Fourthline',
      wm_app: 'Wealth Manager Application',
    } satisfies Record<UserDocumentSource, string>,
    empty: 'This employee has no documents yet',
  },
  actions: {
    create: {
      title: 'Create employee',
      success: 'Employee created',
      failure: 'Employee failed to be created: %{error}',
      steps: {
        '1': 'Personal information',
        '2': 'Financial information',
        '3': 'Contact',
      },
      confirm_merge_employee:
        'Employee with same NIR and birthdate already exists in that company, do you want to update his affiliations and data ?',
    },
    edit: {
      title: 'Edit employee',
      success_edit_request: 'Employee edit request created',
      success_auto_edit_request: 'Employee edited',
      success: 'Employee edited',
      failure: 'Employee failed to be edited: %{error}',
      steps: {
        '1': 'Personal information',
        '2': 'Financial information',
        '3': 'Contact',
        '4': 'Request',
      },
      validation: {
        email_same_as_secondary_email: 'Primary and secondary emails cannot be the same',
      },
    },
    bulk_create: {
      label: 'Bulk upload',
      no_employees_affected: 'No employees affected by this bulk upload.',
      success: 'Employees updated',
      failure: 'Employees failed to be updated: \n%{error}',
      errors: {
        header_line:
          'Please verify the data on the file provided, the header should be place before line 3.',
        file_start_line: 'Please verify the data on the file provided, it should start on line 3.',
        max_lines_exceeded: `The file has data after line ${EMPLOYEES_BULK_CREATE_FILE_MAX_LINE.toLocaleString('en-US')}, the maximum allowed. Please split it into several files.`,
        user_without_mdlr_account:
          'One of the imported employee have an already existing user account without mdlr account.',
        provide_email_address:
          'One (or more) of the imported employee have an already existing user account, please provide the corresponding email address in the file.',
      },
      save: 'Upload file',
      download_template_with_macro: 'Download an empty template file here - with macro',
      download_template_without_macro: 'Download an empty template file here - without macro',
      option_title: 'Option',
      automatic_onboarding_description:
        'Automatic Onboarding will directly send the invite and allow employee to log into their employee spaces while manual onboarding will require you to send the invite first.',
      automatic_onboarding_standard_contracts_note:
        'Standard contracts are always set to Automatic onboarding',
      header_1: 'Download the template',
      header_2: 'Complete your file',
      header_3: 'Upload your completed file',
      description:
        'Add your data to the file by following the guide. Run controls to make sure all everything is correct.',
      fields: {
        file: 'File',
        company_id: 'Company ID',
      },
      sections: {
        company_id: 'Company %{companyId}',
        upload: {
          title: 'Create Employees via bulk upload',
        },
        overwrites: {
          title: 'Review the format',
          description:
            'It looks like some of the fields in your file will override existing data. Please review the highlighted fields to ensure they are correct.',
          ignored_fields_warning:
            'Some fields were not updated because the employee controls them.',
          mixed_ignored_info:
            'Some fields were not updated because the employee controls them. Only company-controlled fields will be overwritten.',
          cta: 'Overwrite',
          cancel: 'Cancel upload',
          subtitle: 'Overrides',
        },
        results: {
          title: 'Results of employees bulk upload - Company id : %{companyId}',
          subtitle:
            'Your file is currently being processed. Please wait while we create and edit the employee records.',
          finished: 'Your file has been fully processed.',
          no_error: 'No error',
          status: {
            pending: 'PENDING',
            running: 'RUNNING',
            finished: 'FINISHED',
            created: 'Created',
            updated: 'Updated',
            error: 'Error',
            in_progress: 'In progress',
          },
          line: 'Line %{line}',
          actions: {
            created: 'CREATE',
            updated: 'UPDATE',
          },
          errors: {
            email_closed_account:
              'Error when trying to update email address, please verify that the email provided is not already linked to an existing closed account.',
            employee_verify_nir_ntt:
              'Error when finding employee account, please compare the NIR or NTT value from the file provided and the value we have in our system.',
          },
        },
        validation_errors: {
          title: 'Review the format',
          description:
            'It appears that some fields in your file are in an incorrect format. Please review the file and correct it before proceeding with the upload.',
          cancel: 'Cancel upload',
          subtitle: 'Incorrect format',
          info: 'You can hover your mouse on any highlighted value to see the details.',
          ignored: 'The employee has already validated this data',
          ignored_email:
            'These emails will not be updated, because the affected employee has already signed in.',
          required: 'Missing required data.',
          field_incorrect: '(%{field} incorrect)',
          field_not_provided: '(%{field} not provided)',
          fields: {
            contract_ids: {
              one_required: 'At least one contract ID is required.',
              missing_taiga_policy_holder_id_for_er:
                'Cannot affiliate to an ER contract without a Taiga policy holder ID.',
            },
            csp_code: {
              invalid_csp_code:
                "This CSP code does not match the configuration on plan's rule for matching contribution.",
            },
            date_of_birth: {
              min_legal_age: 'Employee age must be higher than zero',
            },
            email: {
              database_already_used: 'Email is already used in database.',
              file_already_used: 'Email is already used in file.',
            },
            leaving_reason: {
              not_empty_status_old:
                'Leaving reason should not be empty when status is different than "Présent".',
              not_empty_work_ending_date_set:
                'Leaving reason should not be empty when work_ending_date is set.',
              retirement_status_retired:
                'Leaving reason should be set to "Retraite" when status is "Retraité".',
            },
            ntt: {
              file_already_used: 'NTT is already used in file.',
            },
            phone_number: {
              database_already_used: 'Phone number is already used in database.',
              file_already_used: 'Phone number is already used in file.',
            },
            registration_number: {
              database_already_used:
                'Registration number is already used in this company in database.',
              file_already_used: 'Registration number is already used in file.',
            },
            status: {
              current_work_ending_date_future:
                'Status should be set to "Présent" when work_ending_date is set in future.',
              old_work_ending_date_past:
                'Status should be set to "Sorti" or "Retraité" when work_ending_date is set in past.',
              retired_leaving_reason_retirement:
                'Status should be set to "Retraité" when leaving_reason is "Retraite".',
            },
            taiga_policy_holder_id: {
              database_already_used: 'Taiga policy holder ID is already used in database.',
              file_already_used: 'Taiga policy holder ID is already used in file.',
            },
            work_ending_date: {
              later_work_starting_date: 'Work ending date must be later than work starting date.',
              not_empty_leaving_reason_set:
                'Work ending date should not be empty when leaving_reason is set.',
              not_empty_status_old:
                'Work ending date should not be empty when status is different than "Présent".',
            },
            work_starting_date: {
              min_legal_age: 'Work starting date must be higher than zero.',
              work_starting_date_max: 'Work starting date cannot be in the future.',
            },
          },
        },
      },
    },
    export_multi_company: {
      label: 'Export (with Company ID)',
    },
    send_invite: {
      label: 'Send invite',
      confirm: 'Are you sure you want to send invitation to employee ?',
      confirm_paper_mail: 'The employee will receive a paper mail since no email was provided.',
      success: 'Invitation sent',
      failure: 'Invitation failed to be sent: \n%{error}',
      disabled_company_not_verified: 'Invite cannot be sent until the company is verified',
      disabled_already_activated: 'Employee has already been fully activated',
      disabled_user_closed: 'User account is closed',
    },
    bulk_invite: {
      label: 'Send invites',
      confirm: 'Are you sure you want to send invitations to selected employees?',
      success: 'Invitations sent',
      failure: 'Invitations failed to be sent: \n%{error}',
    },
  },
  labels: {
    identity: 'Identity',
    tax_info: 'Tax info',
    nir_helper: 'This number is visible on the National Insurance card',
    nir_not_yet: 'NIR missing',
    nin_not_yet: 'No fiscal identification number yet',
    address: 'Home Address',
    contact: 'Contact',
    work_info: 'Work Information',
    bank_info: 'Banking Information',
    other: 'Other',
  },
  fields: {
    id: 'Employee ID',
    company_id: 'Company ID',
    company_name: 'Company Name',
    user_id: 'User ID',
    firstname: 'First Name',
    lastname: 'Last Name',
    name_in_use: 'Name in use',
    gender: 'Title',
    email: 'Email',
    secondary_email: 'Secondary email for 2FA',
    email_state: 'Email state',
    secondary_email_state: 'Secondary email state',
    phone_number: 'Phone Number',
    status: 'Employment status',
    street: 'Street',
    postalcode: 'Postal code',
    city: 'City',
    country: 'Country',
    language: 'Language',
    created_at: 'Created at',
    updated_at: 'Updated at',
    last_invitation_sent_at: 'Last invitation sent at',
    date_of_birth: 'Date of birth',
    place_of_birth: 'Place of birth',
    nationality: 'Nationality',
    tax_number: 'National Insurance Number',
    work_starting_date: 'Work starting date',
    investor_profile: 'PERECOI investor profile',
    pei_investor_profile: 'PEI investor profile',
    work_ending_date: 'Work ending date',
    leaving_reason: 'Reason for leaving ',
    tax_residence_code: 'Tax residence code',
    net_employee_deposits: 'Net Employee Deposits ',
    net_employer_contributions: 'Net Employer’s Contributions',
    kyb_status: 'KYB status',
    retirement_date: 'Retirement date',
    social_status: 'Social Status',
    role_in_company: 'Role in company',
    csp_code: 'CSP code',
    registration_number: 'Registration number',
    country_of_birth: 'Country of birth',
    nir: 'NIR',
    ntt: 'NTT',
    is_migrated: 'Is migrated from AXA',
    account_status: 'Account status',
    eckert_status: 'Eckert status',
    eckert_status_changed_at: 'Eckert status changed at',
    last_activity_at: 'Last activity at',
    account_closed_at: 'Account closed at',
    account_closed_reason: 'Account closed reason',
    approbation_state: 'Approbation state',
    ghost_login: 'Ghost login',
    is_phone_number_user_controlled: 'Is phone number user controlled',
    is_email_user_controlled: 'Is email user controlled',
    is_mailing_address_user_controlled: 'Is mailing address user controlled',
    is_address_user_controlled: 'Is address user controlled',
    is_retirement_age_user_controlled: 'Is retirement age user controlled',
    is_deceased: 'Deceased',
    date_of_decease: 'Date of decease',
    taiga_policy_holder_id: 'Taiga Policy Holder ID',
    contract_ids: 'Contract affiliations',
    contract_types: 'Contract types',
    postcode_of_birth: 'Postcode of birth',
    digital_comms_preferences_id: 'Digital Comms Preferences ID',
    automatic_onboarding: 'Automatic Onboarding',
    is_activated_by_company: 'Activated by the company',
    activation_code_is_used: 'Activated by the employee',
    personal_details_confirmed: 'Employee confirmed their details on first sign in',
    activation_code: 'Activation code',
    has_activation_code: 'Has activation code',
    external_id_web_code: 'External ID web code',
    activation_code_expires_at: 'Activation code expires after',
    activation_code_is_external: 'External activation code',
    show_activation_code: 'Show activation code',
    hide_activation_code: 'Hide activation code',
    no_activation_code: 'No activation code',
    show_external_id_web_code: 'Show external ID web code',
    hide_external_id_web_code: 'Hide external ID web code',
    no_external_id_web_code: 'No external ID web code',
    has_last_otp: 'Has valid OTP',
    last_otp: 'Last valid OTP',
    show_last_otp: 'Show last valid OTP',
    hide_last_otp: 'Hide last valid OTP',
    no_last_otp: 'No last valid OTP',
    products: 'Product coverage',
    is_npai: 'Is NPAI',
    is_npai_mailing: 'Is NPAI Mailing',
    id_external: 'ID External Axa',
    id_external_taiga: 'ID External Taiga',
    is_er_eligible: 'ER eligible',
    is_campaigns_blocked: 'Is blocked for campaigns',
    is_campaigns_blocked_regulatory: 'Is blocked for campaigns (regulatory)',
    is_user_closed_or_archived: 'Is root user closed or archived',
  } satisfies Record<
    | keyof (GetB2bEmployeesOneResponse & PostCreateB2bEmployeeRequest)
    | 'ghost_login'
    | 'activation_code'
    | 'external_id_web_code'
    | 'last_otp'
    | 'show_last_otp'
    | 'no_last_otp'
    | 'hide_last_otp'
    | 'show_activation_code'
    | 'hide_activation_code'
    | 'no_activation_code'
    | 'show_external_id_web_code'
    | 'hide_external_id_web_code'
    | 'no_external_id_web_code',
    string
  >,
  notifications: {
    last_otp_fetch_error: 'Unable to fetch the last OTP',
    last_otp_unavailable: 'Could not get the code, it has expired or was already used',
    activation_code_fetch_error: 'Unable to fetch the activation code',
    activation_code_unavailable: 'Could not get the activation code',
  },
  portfolio: {
    fields: {
      fund_name: 'Fund Name',
    },
    compartments: {
      choose: 'Choose compartment',
      all: 'All compartments',
      c1_deductible_voluntary_contributions: 'Voluntary contributions (Deductible)',
      c1_non_deductible_voluntary_contributions: 'Voluntary contributions (Not deductible)',
      c2_collective_contributions: 'Employee savings',
      c3_mandatory_contributions: 'Mandatory contributions',
    },
  },
  choices: {
    status,
    social_status: socialStatus,
    leaving_reason: leavingReason,
    tax_residence_code: {
      tax_resident: 'Tax resident',
      non_tax_resident: 'Non-tax resident',
      resident_exempt: 'Resident Exempt',
    } satisfies Record<Exclude<GetB2bEmployeesOneResponse['tax_residence_code'], null>, string>,
    gender: {
      male: 'Mr',
      female: 'Mrs',
    },
    language: {
      en: 'English',
      fr: 'French',
      pl: 'Polish',
      es: 'Spanish, Castilian',
      de: 'German',
      it: 'Italian',
      sv: 'Swedish',
      lv: 'Latvian',
      lt: 'Lithuanian',
      pt: 'Portuguese',
      ro: 'Romanian, Moldavian, Moldovan',
      sk: 'Slovak',
      sl: 'Slovene',
      cs: 'Czech',
      bg: 'Bulgarian',
      hr: 'Croatian',
      da: 'Danish',
      fi: 'Finnish',
      el: 'Greek (modern)',
      hu: 'Hungarian',
    } satisfies Record<Lowercase<Exclude<GetB2bEmployeesOneResponse['language'], null>>, string>,
    account_status: {
      active: 'Active',
      closed: 'Closed',
    } satisfies Record<GetB2bEmployeesOneResponse['account_status'], string>,
    eckert_status: {
      not_activated: 'Not activated',
      active: 'Active',
      inactive: 'Inactive',
      desherence: 'Desherence',
      attente_depose_cdc: 'Awaiting CDC deposit',
      depose_cdc_pee: 'PEE deposited to CDC',
      depose_cdc: 'Deposited to CDC',
    } satisfies Record<GetB2bEmployeesOneResponse['eckert_status'], string>,
    email_state: {
      blocked: 'blocked',
      permanently_blocked: 'permanently blocked',
      invalid: 'invalid',
    } satisfies Record<Exclude<GetB2bEmployeesOneResponse['email_state'], null>, string>,
    email_state_tooltip: {
      blocked:
        'Delivery failed or reported as spam. Ask the user to whitelist our address, then manually lift the block.',
      permanently_blocked:
        'This address bounced or was reported as spam again after its block was lifted, so it cannot be unblocked anymore. Ask the user for a different email address and update it on their profile.',
      invalid:
        'Unable to deliver email to this address. Verify the address or request a new one from the user.',
    } satisfies Record<Exclude<GetB2bEmployeesOneResponse['email_state'], null>, string>,
  },
  validation: {
    csp_code: 'The CSP code must be one of them : %{cspCodes} (configured in MC rules).',
    tax_number: 'TIN must be 13 characters length',
    dob: 'Employee age must be higher than zero',
    work_ending_date: 'Work ending date must be after work starting date',
    work_starting_date: 'Work starting date must be higher than zero',
    work_starting_date_max: 'Work starting date cannot be in the future',
    er_requires_taiga_policy_holder_id:
      'An employee cannot be affiliated to an ER contract without a Taiga policy holder ID',
    nir: {
      format_invalid: 'Invalid NIR: must contain exactly 13 or 15 numeric digits',
      gender_invalid: 'Invalid NIR: the first digit must be 1 (Male) or 2 (Female)',
      gender_inconsistent:
        'Invalid NIR: the first digit must be 1 (Male) or 2 (Female). Please check consistency with the title (Mr/Ms)',
      year_invalid: 'Invalid NIR: incorrect year of birth',
      year_inconsistent:
        'Invalid NIR: inconsistent with the provided year of birth. Please check the 2nd and 3rd digit of the NIR',
      month_invalid: 'Invalid NIR: incorrect month of birth',
      month_inconsistent:
        'Invalid NIR: inconsistent with the provided month of birth. Please check the 4th and 5th digits of the NIR',
      department_invalid: 'Invalid NIR: incorrect department of birth',
      order_number_invalid:
        'Invalid NIR: civil registration number cannot be null. Please check the 11th, 12th and 13th digits of the NIR',
      control_key_invalid: 'Invalid NIR: the NIR key is not consistent with the provided data',
      invalid: 'Invalid NIR',
      file_already_used: 'NIR is already used in file',
    },
  },
};
